import {formatYAxis} from '../../util/plotHelpers/axis';
import type {ArtifactVersionMetricPlotPoint} from './artifactVersionMetricAdapters';
import {
  buildVersionLabelByXValue,
  formatArtifactVersionMetricVersionTick,
  isArtifactVersionMetricDualMetricScatter,
} from './artifactVersionMetricChartUtils';
import type {ArtifactVersionMetricPlotReady} from './artifactVersionMetricPlotModel';

export type ArtifactVersionMetricHintEntry = {title: string; value: string};

export function formatArtifactVersionMetricValue(value: number | null): string {
  if (value == null) {
    return '—';
  }
  if (!Number.isFinite(value)) {
    return String(value);
  }
  return formatYAxis(value);
}

export function getArtifactVersionMetricVersionXTickValues(
  plot: ArtifactVersionMetricPlotReady
): number[] | undefined {
  const xAxisMode = plot.config.xAxisMode ?? 'selectionIndex';
  if (
    isArtifactVersionMetricDualMetricScatter(plot.config) ||
    xAxisMode !== 'selectionIndex'
  ) {
    return undefined;
  }

  return plot.rows.map((_row, index) => index);
}

export function createArtifactVersionMetricXTickFormatter(
  plot: ArtifactVersionMetricPlotReady
): (value: number) => string {
  const versionLabelByXValue = buildVersionLabelByXValue(plot.rows);

  return value => {
    if (isArtifactVersionMetricDualMetricScatter(plot.config)) {
      return formatArtifactVersionMetricValue(value);
    }
    return formatArtifactVersionMetricVersionTick(value, versionLabelByXValue);
  };
}

export function createArtifactVersionMetricHintFormatter(
  plot: ArtifactVersionMetricPlotReady
): (value: ArtifactVersionMetricPlotPoint) => ArtifactVersionMetricHintEntry[] {
  const {config} = plot;
  const yTitle =
    config.metricName != null && config.metricName !== ''
      ? config.metricName
      : 'Value';
  const xTitle =
    config.xMetricName != null && config.xMetricName !== ''
      ? config.xMetricName
      : 'X';

  return value => {
    const row = value.artifactRow;
    const entries: ArtifactVersionMetricHintEntry[] = [
      {title: 'Version', value: row.label},
    ];

    if (isArtifactVersionMetricDualMetricScatter(config)) {
      entries.push(
        {
          title: xTitle,
          value: formatArtifactVersionMetricValue(
            value.xMetricValue ?? value.x
          ),
        },
        {title: yTitle, value: formatArtifactVersionMetricValue(row.value)}
      );
    } else {
      entries.push({
        title: yTitle,
        value: formatArtifactVersionMetricValue(row.value),
      });
    }

    if (row.sourceRunName != null && row.sourceRunName !== '') {
      entries.push({title: 'Source run', value: row.sourceRunName});
    }
    if (row.sourceProjectName != null && row.sourceProjectName !== '') {
      entries.push({title: 'Source project', value: row.sourceProjectName});
    }
    if (row.historyStep != null) {
      entries.push({
        title: 'History step',
        value: String(row.historyStep),
      });
    }

    return entries;
  };
}
